import { useState, useRef, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { T } from '../lib/tokens'
import { supabase } from '../lib/supabase'
import { chatTitle, askLake, chatToMemo, modelLabel as apiModelLabel } from '../lib/api'
import { useChatSessions } from '../lib/hooks'
import { CopyButton } from './ui'

interface Source { item_id: string; title: string }
interface Message {
  role: 'user' | 'assistant'
  content: string
  sources?: Source[]
  actions?: string[]
  model?: string | null
  ts?: string
}

function modelLabel(model?: string | null): string {
  if (!model) return ''
  return apiModelLabel(model)
}

const SUGGESTIONS = [
  '今週保存した記事を3行でまとめて',
  'tech カテゴリのトレンドは？',
  '最近保存したレシピを教えて',
  'TODO になっているメモを一覧で',
]

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export function GeminiDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: sessions = [] } = useChatSessions()
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput]       = useState('')
  const [loading, setLoading]   = useState(false)
  const [saving, setSaving]     = useState(false)
  const [saveMsg, setSaveMsg]   = useState<string | null>(null)
  const [useContext, setUseContext] = useState(true) // 前の会話を踏まえる
  const [useMemos, setUseMemos]     = useState(true) // 保存メモを参照する
  const bottomRef               = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  function newChat() {
    setSessionId(null)
    setMessages([])
    setShowHistory(false)
    setSaveMsg(null)
  }

  async function saveAsMemo(sid: string) {
    if (saving) return
    setSaving(true)
    setSaveMsg('💾 メモとして保存中…')
    try {
      const r = await chatToMemo(sid)
      if (r.ok && r.item_id) {
        setSaveMsg(`✓ メモとして保存しました（カテゴリ: ${r.category ?? '?'}）`)
      } else {
        setSaveMsg('⚠️ 保存に失敗しました')
      }
    } catch {
      setSaveMsg('⚠️ 保存に失敗しました')
    } finally {
      setSaving(false)
      setTimeout(() => setSaveMsg(null), 5000)
    }
  }

  async function openSession(id: string) {
    setShowHistory(false)
    setSessionId(id)
    const { data } = await supabase
      .from('ml_chats').select('*').eq('session_id', id).order('created_at', { ascending: true })
    setMessages((data ?? []).map((r: any) => ({
      role: r.role, content: r.content, sources: r.sources ?? [], actions: [], model: r.model, ts: r.created_at,
    })))
  }

  async function deleteSession(id: string, e: { stopPropagation: () => void }) {
    e.stopPropagation()
    if (!confirm('このスレッドを削除しますか？')) return
    await supabase.from('ml_chat_sessions').delete().eq('id', id) // ml_chats も CASCADE 削除
    if (sessionId === id) newChat()
    qc.invalidateQueries({ queryKey: ['chat-sessions'] })
  }

  async function send(text: string) {
    const q = text.trim()
    if (!q || loading) return
    const isFirst = messages.length === 0 && !sessionId
    let sid = sessionId

    setMessages(prev => [...prev, { role: 'user', content: q, ts: new Date().toISOString() }])
    setInput('')
    setLoading(true)

    try {
      // セッションが無ければ作成（user_id を明示的に渡す）
      if (!sid) {
        const { data: { session } } = await supabase.auth.getSession()
        const { data, error } = await supabase
          .from('ml_chat_sessions').insert({ title: null, user_id: session?.user?.id }).select('id').single()
        if (error) throw error
        sid = data.id as string
        setSessionId(sid)
      }
      // ユーザー発言を保存
      await supabase.from('ml_chats').insert({ session_id: sid, role: 'user', content: q })

      // 回答取得（文脈・メモ参照はトグルに従う）
      const history = useContext ? messages.slice(-12).map(m => ({ role: m.role, content: m.content })) : []
      const data = await askLake(q, { history, useMemos })
      const answer  = data.answer ?? JSON.stringify(data, null, 2)
      const sources = data.sources ?? []
      const actions = data.actions ?? []
      const model   = data.model ?? null
      setMessages(prev => [...prev, { role: 'assistant', content: answer, sources, actions, model, ts: new Date().toISOString() }])

      // 回答を保存 + スレッドの更新時刻
      await supabase.from('ml_chats').insert({ session_id: sid, role: 'assistant', content: answer, sources, model })
      await supabase.from('ml_chat_sessions').update({ updated_at: new Date().toISOString() }).eq('id', sid)

      // 初回ならタイトルを Gemini で生成
      if (isFirst) {
        try {
          const { title } = await chatTitle(q)
          if (title) await supabase.from('ml_chat_sessions').update({ title }).eq('id', sid)
        } catch { /* タイトル生成失敗は無視 */ }
      }
      qc.invalidateQueries({ queryKey: ['chat-sessions'] })
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '⚠️ 通信エラーが発生しました。しばらく待ってから再試行してください。',
      }])
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* バックドロップ */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.2s', zIndex: 40,
        }}
      />

      {/* ドロワー */}
      <aside style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 400, maxWidth: '90vw',
        background: T.bgPanel,
        borderLeft: `1px solid ${T.borderHi}`,
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s cubic-bezier(.2,.7,.3,1)',
        boxShadow: '-12px 0 40px rgba(0,0,0,0.4)',
        display: 'flex', flexDirection: 'column',
        zIndex: 41, fontFamily: T.font,
      }}>

        {/* ヘッダー */}
        <div style={{
          padding: '12px 14px',
          borderBottom: `1px solid ${T.border}`,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: 7, flexShrink: 0,
            background: 'linear-gradient(135deg, oklch(0.72 0.16 258) 0%, oklch(0.60 0.20 278) 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 14,
          }}>✦</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Ask the Lake</div>
            <div style={{ fontSize: 10, color: T.textMute, fontFamily: T.fontMono }}>
              Gemini · 全メモ横断 · ⌘J
            </div>
          </div>
          <button onClick={newChat} title="新しい会話" style={hdrBtn}>＋ 新規</button>
          <button onClick={() => setShowHistory(v => !v)} title="会話履歴" style={{ ...hdrBtn, color: showHistory ? T.accent : T.textDim }}>≡ 履歴</button>
          {sessionId && !showHistory && messages.some(m => m.role === 'assistant') && (
            <button onClick={() => saveAsMemo(sessionId)} disabled={saving} title="この会話をメモとして保存（自動分類）" style={{ ...hdrBtn, color: saving ? T.textMute : T.ok }}>
              {saving ? '💾…' : '📝 メモ化'}
            </button>
          )}
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none',
            color: T.textDim, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 4px',
          }}>×</button>
        </div>

        {/* メモ化ステータス */}
        {saveMsg && (
          <div style={{
            padding: '5px 14px', fontSize: 11, fontFamily: T.fontMono,
            color: saveMsg.startsWith('✓') ? T.ok : T.textDim,
            background: saveMsg.startsWith('✓') ? T.okSoft : T.bgActive,
            borderBottom: `1px solid ${T.border}`,
          }}>{saveMsg}</div>
        )}

        {/* 履歴パネル */}
        {showHistory ? (
          <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 9, fontFamily: T.fontMono, color: T.textMute, letterSpacing: '0.12em', marginBottom: 4 }}>
              スレッド履歴（{sessions.length}）
            </div>
            {sessions.length === 0 && (
              <div style={{ fontSize: 12, color: T.textMute, padding: '12px 4px' }}>まだ会話はありません。</div>
            )}
            {sessions.map(s => (
              <div key={s.id} onClick={() => openSession(s.id)} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: s.id === sessionId ? T.accentSoft : T.bgRaised,
                border: `1px solid ${s.id === sessionId ? T.accentBorder : T.border}`,
                borderRadius: 6, padding: '9px 11px', cursor: 'pointer',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 12, color: T.text, fontWeight: 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{s.title || '無題のスレッド'}</div>
                  <div style={{ fontSize: 10, color: T.textMute, fontFamily: T.fontMono, marginTop: 2 }}>
                    {fmtDate(s.updated_at)}
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); saveAsMemo(s.id) }} title="メモとして保存" disabled={saving} style={{
                  background: 'transparent', border: 'none', color: T.textMute,
                  cursor: 'pointer', fontSize: 13, padding: '2px 4px', flexShrink: 0,
                }}>📝</button>
                <button onClick={(e) => deleteSession(s.id, e)} title="削除" style={{
                  background: 'transparent', border: 'none', color: T.textMute,
                  cursor: 'pointer', fontSize: 14, padding: '2px 4px', flexShrink: 0,
                }}>×</button>
              </div>
            ))}
          </div>
        ) : (
        <>
        {/* メッセージ一覧 */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: 14,
          display: 'flex', flexDirection: 'column', gap: 14,
        }}>
          {/* 初期画面 */}
          {messages.length === 0 && (
            <div style={{ padding: '16px 4px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.7 }}>
                MemoLake 全体に対して質問できます。<br />
                カテゴリ・タグ・期間で絞り込んで聞いてみてください。
              </div>
              <div>
                <div style={{
                  fontSize: 9, fontFamily: T.fontMono, color: T.textMute,
                  letterSpacing: '0.12em', marginBottom: 8,
                }}>SUGGESTED</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {SUGGESTIONS.map(s => (
                    <button key={s} onClick={() => send(s)} style={{
                      textAlign: 'left', background: T.bgRaised,
                      border: `1px solid ${T.border}`, borderRadius: 6,
                      padding: '9px 12px', color: T.textDim, fontSize: 12,
                      cursor: 'pointer', fontFamily: T.font, lineHeight: 1.4,
                    }}>{s}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* メッセージ */}
          {messages.map((msg, i) => (
            <div key={i} style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
              gap: 8,
            }}>
              <div style={{ display: 'flex', gap: 8, maxWidth: '90%' }}>
                {msg.role === 'assistant' && (
                  <div style={{
                    width: 22, height: 22, borderRadius: 5, flexShrink: 0, marginTop: 2,
                    background: 'linear-gradient(135deg, oklch(0.72 0.16 258) 0%, oklch(0.60 0.20 278) 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: 11,
                  }}>✦</div>
                )}
                <div style={{
                  background: msg.role === 'user' ? T.accentSoft : T.bgRaised,
                  border: `1px solid ${msg.role === 'user' ? T.accentBorder : T.border}`,
                  borderRadius: 8, padding: '9px 12px',
                  fontSize: 12, color: T.text, lineHeight: 1.7,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {renderMarkdown(msg.content)}
                </div>
              </div>

              {/* ソース */}
              {msg.sources && msg.sources.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%', paddingLeft: 30 }}>
                  <div style={{ fontSize: 9, fontFamily: T.fontMono, color: T.textMute, letterSpacing: '0.1em' }}>
                    SOURCES
                  </div>
                  {msg.sources.map(src => (
                    <a key={src.item_id} href={`/items/${src.item_id}`} style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      background: T.bgPanel, border: `1px solid ${T.border}`,
                      borderRadius: 5, padding: '5px 9px',
                      fontSize: 11, color: T.textDim, textDecoration: 'none',
                    }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {src.title || src.item_id}
                      </span>
                      <span style={{ fontSize: 10, color: T.textMute }}>↗</span>
                    </a>
                  ))}
                </div>
              )}

              {/* アクション提案 */}
              {msg.actions && msg.actions.length > 0 && (
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', paddingLeft: 30 }}>
                  {msg.actions.map(action => (
                    <button key={action} onClick={() => send(action)} style={{
                      background: 'transparent',
                      border: `1px solid ${T.border}`,
                      borderRadius: 12, padding: '3px 10px',
                      fontSize: 11, color: T.textDim, cursor: 'pointer',
                      fontFamily: T.fontMono,
                    }}>{action}</button>
                  ))}
                </div>
              )}

              {/* 日時・モデル・コピー */}
              <span style={{ fontSize: 10, fontFamily: T.fontMono, color: T.textMute, padding: '0 2px', display: 'flex', alignItems: 'center', gap: 8 }}>
                {msg.ts && <span>{fmtDate(msg.ts)}</span>}
                {msg.role === 'assistant' && msg.model && <span>{modelLabel(msg.model)}</span>}
                <CopyButton text={msg.content} />
              </span>
            </div>
          ))}

          {/* ローディング */}
          {loading && (
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{
                width: 22, height: 22, borderRadius: 5, flexShrink: 0,
                background: 'linear-gradient(135deg, oklch(0.72 0.16 258) 0%, oklch(0.60 0.20 278) 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 11,
              }}>✦</div>
              <div style={{
                background: T.bgRaised, border: `1px solid ${T.border}`,
                borderRadius: 8, padding: '9px 14px',
                display: 'flex', gap: 5, alignItems: 'center',
              }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 6, height: 6, borderRadius: '50%', background: T.textMute,
                    animation: `ml-bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }} />
                ))}
                <style>{`@keyframes ml-bounce{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-6px)}}`}</style>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* 入力欄 */}
        <div style={{ borderTop: `1px solid ${T.border}`, padding: 12 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <button onClick={() => setUseContext(v => !v)} title="前の会話を踏まえて続ける" style={toggleChip(useContext)}>
              {useContext ? '🧵 文脈 ON' : '文脈 OFF'}
            </button>
            <button onClick={() => setUseMemos(v => !v)} title="保存メモ全体を参照する" style={toggleChip(useMemos)}>
              {useMemos ? '📚 メモ参照 ON' : 'メモ参照 OFF'}
            </button>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: T.bgRaised, border: `1px solid ${T.borderHi}`,
            borderRadius: 6, padding: '8px 10px',
          }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send(input)}
              placeholder="質問を入力… (⌘J で開閉)"
              autoFocus={open}
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                color: T.text, fontSize: 13, fontFamily: T.font,
              }}
            />
            <button
              onClick={() => send(input)}
              disabled={!input.trim() || loading}
              style={{
                background: input.trim() ? T.accent : T.bgHover,
                border: 'none', borderRadius: 4,
                color: input.trim() ? '#0a0b0e' : T.textMute,
                padding: '4px 10px', fontSize: 11, fontWeight: 600,
                cursor: input.trim() ? 'pointer' : 'default',
                fontFamily: T.fontMono,
              }}
            >↵ ask</button>
          </div>
        </div>
        </>
        )}
      </aside>
    </>
  )
}

const hdrBtn: React.CSSProperties = {
  background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 5,
  color: T.textDim, cursor: 'pointer', fontSize: 11, fontFamily: T.fontMono,
  padding: '4px 8px', flexShrink: 0,
}

function toggleChip(on: boolean): React.CSSProperties {
  return {
    background: on ? T.accentSoft : 'transparent',
    border: `1px solid ${on ? T.accentBorder : T.border}`,
    color: on ? 'oklch(0.85 0.12 258)' : T.textMute,
    borderRadius: 12, padding: '3px 10px', fontSize: 11, cursor: 'pointer', fontFamily: T.fontMono,
  }
}

// **太字** と \n を簡易レンダリング
function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n')
  return lines.map((line, li) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/)
    return (
      <span key={li}>
        {parts.map((part, pi) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={pi} style={{ color: 'inherit', fontWeight: 700 }}>{part.slice(2, -2)}</strong>
          }
          return <span key={pi}>{part}</span>
        })}
        {li < lines.length - 1 && <br />}
      </span>
    )
  })
}

export function useGeminiDrawer() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault()
        setOpen(prev => !prev)
      }
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return { open, setOpen }
}
